import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashLayout from "@/layouts/DashLayout";
import TabsMenu from "./Tabs/TabsMenu";
import TabsProduct from "./Tabs/TabsProduct";
import TabsOption from "./Tabs/TabsOption";
import TabsFormula from "./Tabs/TabsFormula";
import TabsDailyDish from "./Tabs/TabsDailyDish";
import PageTitle from "@/components/PageTitle";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import { Roles } from "@/redux/services/user/user.type";
import ListeRestaurantTables from "@/components/Dashboard/ListeRestaurantTables";
import { useAppSelector } from "@/redux/features/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { BookOpen, Settings, Armchair, ClipboardList, ChefHat, Utensils } from "lucide-react";
import { cn } from "@/lib/utils";
import { parseMenusTab } from "@/lib/navigation";

const tabs = [
  { id: "menu", label: "Menus", icon: BookOpen },
  { id: "product", label: "Plats", icon: Utensils },
  { id: "daily_dish", label: "Plats du jour", icon: ChefHat },
  { id: "formula", label: "Formules", icon: ClipboardList },
  { id: "option", label: "Options", icon: Settings },
  { id: "table", label: "Tables", icon: Armchair },
] as const;

type TabId = (typeof tabs)[number]["id"];

const ListMenus = () => {
  const { currentUser, currentRestaurant } = useAppSelector((s) => s.user);
  const { canManage } = usePermissions();
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const needsRestaurantSelection = isOwner && !currentRestaurant?.id;
  // ?tab= picks the tab (e.g. a dish picked in the global search opens Plats).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = parseMenusTab(searchParams.get("tab"));
  const [activeTab, setActiveTab] = useState<TabId>(tabParam ?? "menu");
  useEffect(() => {
    if (tabParam) setActiveTab(tabParam);
  }, [tabParam, searchParams]);

  const canManageSettings = canManage("settings");
  const visibleTabs = tabs.filter((tab) => tab.id !== "table" || canManageSettings);

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["menu.view"]}
      >
        <div className="space-y-6">
          <PageTitle title="Menus" description="Configurez vos menus, options et tables" />

          {needsRestaurantSelection ? (
            <div className="bg-card rounded-lg border border-border flex items-center justify-center h-64 text-muted-foreground">
              <p>Sélectionnez un restaurant pour voir les menus</p>
            </div>
          ) : (
            <>
              {/* Horizontal tabs */}
              <div className="border-b border-border overflow-x-auto scroll-hidden">
                <nav className="flex gap-1 min-w-max">
                  {visibleTabs.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => {
                        setActiveTab(id);
                        // A tab picked by hand drops the search's ?tab/q.
                        if (searchParams.toString()) {
                          setSearchParams({}, { replace: true });
                        }
                      }}
                      className={cn(
                        "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap",
                        activeTab === id
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-card-foreground hover:border-border"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab content */}
              <div>
                {activeTab === "menu" && <TabsMenu />}
                {activeTab === "product" && <TabsProduct />}
                {activeTab === "daily_dish" && <TabsDailyDish />}
                {activeTab === "formula" && <TabsFormula />}
                {activeTab === "option" && <TabsOption />}
                {activeTab === "table" && canManageSettings && <ListeRestaurantTables />}
              </div>
            </>
          )}
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
};

export default ListMenus;
